import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useProfile } from './useProfile';
import { useTransactions } from './useTransactions';
import {
  markOnboardingDoneLocal,
  shouldStartOnboarding,
  type AcquisitionSource,
} from '@/lib/onboarding';

/** Fire-and-forget notification to GoHighLevel (no-op server-side if unconfigured). */
function notifyGhl(event: 'signup' | 'activation') {
  void supabase.functions.invoke('notify-ghl', { body: { event } }).catch(() => {
    /* non-blocking: GHL delivery must never break the onboarding UX */
  });
}

export function useOnboarding() {
  const { user } = useAuth();
  const { profile } = useProfile();
  const queryClient = useQueryClient();
  const { transactions, isLoading: isLoadingTransactions } = useTransactions();

  const needsOnboarding = useMemo(
    () =>
      shouldStartOnboarding({
        onboardingCompletedAt: profile?.onboarding_completed_at,
        hasAnyTransactions: (transactions?.length ?? 0) > 0,
        isLoadingTransactions,
      }),
    [profile?.onboarding_completed_at, transactions?.length, isLoadingTransactions],
  );

  const saveAcquisitionSource = useMutation({
    mutationFn: async (source: AcquisitionSource) => {
      if (!user) throw new Error('No user');
      const { error } = await supabase
        .from('profiles')
        .update({ acquisition_source: source })
        .eq('id', user.id);
      if (error) throw error;
      return source;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile', user?.id] });
      notifyGhl('signup');
    },
  });

  const completeOnboarding = useMutation({
    mutationFn: async (initialScore: number | null) => {
      if (!user) throw new Error('No user');
      const now = new Date().toISOString();
      const updates: Record<string, unknown> = { onboarding_completed_at: now };
      if (initialScore != null) {
        updates.initial_foco_score = Math.round(initialScore);
        updates.initial_foco_score_at = now;
      }
      const { error } = await supabase.from('profiles').update(updates).eq('id', user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      markOnboardingDoneLocal();
      queryClient.invalidateQueries({ queryKey: ['profile', user?.id] });
      notifyGhl('activation');
    },
  });

  return {
    profile,
    needsOnboarding,
    acquisitionSource: profile?.acquisition_source ?? null,
    saveAcquisitionSource,
    completeOnboarding,
  };
}
